import {
  ONE_DAY,
  ONE_HOUR,
  ONE_MINUTE,
  ONE_MONTH,
  ONE_SECOND,
  ONE_WEEK,
  ONE_YEAR,
} from "~constants";

export function formatAsDateTimeISO(date: string, time: string) {
  return new Date(`${date}T${time}Z`);
}

export function normalizeDateTime(dateTime: number | string) {
  const date = new Date(dateTime);

  if (isNaN(date.getTime())) {
    throw new Error("Invalid date format");
  }

  return Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes()
  );
}

// Get the start of the week (e.g., Monday) for aligning weekly data
export function getStartOfWeek(date: Date): string {
  // Get the day of the week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
  const dayOfWeek = date.getUTCDay();
  // Calculate the distance to Monday
  const distanceToMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  // Create a new date object set to the start of the week
  const startOfWeek = new Date(date);

  startOfWeek.setUTCDate(date.getUTCDate() - distanceToMonday);
  // Normalize the time to midnight UTC
  startOfWeek.setUTCHours(0, 0, 0, 0);

  return startOfWeek.toISOString().split("T")[0];
}

const NARROW_RTF = new Intl.RelativeTimeFormat("en", {
  numeric: "auto",
  style: "narrow",
});

const SHORT_RTF = new Intl.RelativeTimeFormat("en", {
  numeric: "auto",
  style: "long",
});

function formatRelativeTime(
  delta: number,
  unit: Intl.RelativeTimeFormatUnit
): string {
  switch (unit) {
    case "second":
      return NARROW_RTF.format(Math.round(delta), unit);
    case "minute":
      return NARROW_RTF.format(Math.round(delta), unit);
    case "hour":
      return NARROW_RTF.format(Math.round(delta), unit);
    case "day":
      return SHORT_RTF.format(Math.round(delta), unit);
    case "week":
      return SHORT_RTF.format(Math.round(delta), unit);
    case "month":
      return SHORT_RTF.format(Math.round(delta), unit);
    case "year":
      return SHORT_RTF.format(Math.round(delta), unit);
    default:
      return "just now";
  }
}

export function getRelativeTime(timestamp: number | Date): string {
  const now = new Date();
  const past = new Date(timestamp);
  const diffInSeconds = Math.floor((past.getTime() - now.getTime()) / 1000);

  const units: { unit: Intl.RelativeTimeFormatUnit; seconds: number }[] = [
    { unit: "year", seconds: ONE_YEAR },
    { unit: "month", seconds: ONE_MONTH },
    { unit: "week", seconds: ONE_WEEK },
    { unit: "day", seconds: ONE_DAY },
    { unit: "hour", seconds: ONE_HOUR },
    { unit: "minute", seconds: ONE_MINUTE },
    { unit: "second", seconds: ONE_SECOND },
  ];

  for (const { unit, seconds } of units) {
    const delta = diffInSeconds / seconds;

    if (Math.abs(delta) >= 1 || unit === "second") {
      return formatRelativeTime(Math.round(delta), unit);
    }
  }

  // fallback
  return "just now";
}
